'use client';

// Animated halftone cascade for the hero journey section (canvas, from morfi-cascade).
import { c as _c } from "react/compiler-runtime";
import type { JSX } from 'react';
import { useEffect, useRef, useState } from 'react';
import { computeHeroJourneyCascadeFit, drawHeroJourneyCascadeFrame, getHeroJourneyCascadeIntroProgress, type HeroJourneyCascadeFit, prepareHeroJourneyCascadeGlyphs } from '@/components/support-mainnet/hero-journey-cascade-engine';
import { createHeroJourneyCascadeSpotlightTrail, type HeroJourneyCascadeSpotlightTrail, hostLocalToCascadeArtboard, setHeroJourneyCascadeSpotlightPosition, tickHeroJourneyCascadeSpotlightTrail } from '@/components/support-mainnet/hero-journey-cascade-spotlight';
import { useCursorSpotlight } from '@/hooks/ui/use-cursor-spotlight';
import { useMediaQuery } from '@/hooks/ui/use-media-query';
import { cn } from '@/lib/ui/utils';
const CASCADE_VISIBILITY_THRESHOLD = 0.05;
type HeroJourneyDotMarkProps = {
  className?: string;
};
export function HeroJourneyDotMark(t0) {
  const $ = _c(11);
  const {
    className
  } = t0;
  const hostRef = useRef(null);
  const canvasRef = useRef(null);
  const reducedMotion = useMediaQuery("(prefers-reduced-motion: reduce)");
  const [initialSpotlightTrail] = useState(createHeroJourneyCascadeSpotlightTrail);
  const [initialFit] = useState(_temp);
  const spotlightTrailRef = useRef(initialSpotlightTrail);
  const fitRef = useRef(initialFit);
  let t1;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t1 = position => {
      const fit = fitRef.current;
      const trail = spotlightTrailRef.current;
      if (fit === null || trail === null) {
        return;
      }
      if (position === null) {
        setHeroJourneyCascadeSpotlightPosition(trail, null, performance.now(), fit.scale);
        return;
      }
      const artboard = hostLocalToCascadeArtboard(position.x, position.y, fit);
      setHeroJourneyCascadeSpotlightPosition(trail, artboard, performance.now(), fit.scale);
    };
    $[0] = t1;
  } else {
    t1 = $[0];
  }
  const t2 = !reducedMotion;
  let t3;
  if ($[1] !== t2) {
    t3 = {
      enabled: t2
    };
    $[1] = t2;
    $[2] = t3;
  } else {
    t3 = $[2];
  }
  useCursorSpotlight(hostRef, t1, t3);
  let t4;
  let t5;
  if ($[3] !== reducedMotion) {
    t4 = () => {
      const host = hostRef.current;
      const canvas = canvasRef.current;
      if (host === null || canvas === null) {
        return;
      }
      const hostEl = host;
      const canvasEl = canvas;
      const ctx = canvasEl.getContext("2d");
      if (ctx === null) {
        return;
      }
      const glyphs = prepareHeroJourneyCascadeGlyphs();
      let fit_0 = computeHeroJourneyCascadeFit(0, 0, window.devicePixelRatio);
      fitRef.current = fit_0;
      const spotlightTrail = spotlightTrailRef.current;
      if (spotlightTrail === null) {
        return;
      }
      const paintCtx = ctx;
      const paintTrail = spotlightTrail;
      let raf = 0;
      let running = false;
      let loopStart = 0;
      const resize = function resize() {
        fit_0 = computeHeroJourneyCascadeFit(hostEl.clientWidth, hostEl.clientHeight, window.devicePixelRatio);
        fitRef.current = fit_0;
        canvasEl.width = Math.round(fit_0.width * fit_0.dpr);
        canvasEl.height = Math.round(fit_0.height * fit_0.dpr);
      };
      const paint = function paint(timeMs, introProgress) {
        tickHeroJourneyCascadeSpotlightTrail(paintTrail, timeMs);
        drawHeroJourneyCascadeFrame(paintCtx, glyphs, fit_0, timeMs, introProgress, paintTrail);
      };
      function loop(timestamp) {
        if (!running) {
          return;
        }
        if (loopStart === 0) {
          loopStart = timestamp;
        }
        const elapsed = timestamp - loopStart;
        paint(timestamp, getHeroJourneyCascadeIntroProgress(elapsed));
        raf = requestAnimationFrame(loop);
      }
      const start = function start() {
        if (running) {
          return;
        }
        running = true;
        loopStart = 0;
        raf = requestAnimationFrame(loop);
      };
      const stop = function stop() {
        running = false;
        cancelAnimationFrame(raf);
      };
      resize();
      if (reducedMotion) {
        paint(0, 1);
        const resizeObserver = new ResizeObserver(() => {
          resize();
          paint(0, 1);
        });
        resizeObserver.observe(hostEl);
        return () => {
          resizeObserver.disconnect();
        };
      }
      const resizeObserver_0 = new ResizeObserver(() => {
        resize();
      });
      resizeObserver_0.observe(hostEl);
      const intersectionObserver = new IntersectionObserver(entries => {
        for (const entry of entries) {
          if (entry.isIntersecting) {
            start();
          } else {
            stop();
          }
        }
      }, {
        threshold: CASCADE_VISIBILITY_THRESHOLD
      });
      intersectionObserver.observe(hostEl);
      return () => {
        stop();
        resizeObserver_0.disconnect();
        intersectionObserver.disconnect();
      };
    };
    t5 = [reducedMotion];
    $[3] = reducedMotion;
    $[4] = t4;
    $[5] = t5;
  } else {
    t4 = $[4];
    t5 = $[5];
  }
  useEffect(t4, t5);
  let t6;
  if ($[6] !== className) {
    t6 = cn("absolute inset-0 overflow-visible", className);
    $[6] = className;
    $[7] = t6;
  } else {
    t6 = $[7];
  }
  let t7;
  if ($[8] === Symbol.for("react.memo_cache_sentinel")) {
    t7 = <canvas ref={canvasRef} className="absolute inset-0 size-full" />;
    $[8] = t7;
  } else {
    t7 = $[8];
  }
  let t8;
  if ($[9] !== t6) {
    t8 = <div ref={hostRef} aria-hidden="true" className={t6} data-testid="hero-journey-dot-mark">{t7}</div>;
    $[9] = t6;
    $[10] = t8;
  } else {
    t8 = $[10];
  }
  return t8;
}
function _temp() {
  return computeHeroJourneyCascadeFit(0, 0, 1);
}